import { API_URL } from "@/api/application-api";
import { LanguageType } from "@/components/CodeEditor/types";

const API_KEY_PLACEHOLDER = "<API_KEY>";

export type TraceProviderId =
  | "promptlayer-sdk"
  | "opentelemetry"
  | "langchain"
  | "vercel-ai"
  | "pydantic-ai"
  | "openai-agents"
  | "claude-code"
  | "openclaw"
  | "livekit";

interface TraceProviderConfig {
  id: TraceProviderId;
  title: string;
  description: string;
  docsUrl: string;
  logoProvider: string;
  languages: LanguageType[];
  getSnippet: (language: LanguageType, apiKey: string | null) => string;
}

const formatApiKey = (apiKey: string | null) => apiKey ?? API_KEY_PLACEHOLDER;

const getOtlpEndpoint = () => `${API_URL}/v1/traces`;

const BOTH_LANGUAGES: LanguageType[] = ["PYTHON", "JAVASCRIPT"];
const PYTHON_ONLY: LanguageType[] = ["PYTHON"];
const JAVASCRIPT_ONLY: LanguageType[] = ["JAVASCRIPT"];
const BASH_ONLY: LanguageType[] = ["BASH"];

export const TRACE_PROVIDERS: TraceProviderConfig[] = [
  {
    id: "promptlayer-sdk",
    title: "PromptLayer SDK",
    description: "Use @traceable around your LLM calls.",
    docsUrl: "https://docs.promptlayer.com/running-requests/traces",
    logoProvider: "promptlayer",
    languages: BOTH_LANGUAGES,
    getSnippet: (language, apiKey) => {
      const key = formatApiKey(apiKey);

      if (language === "JAVASCRIPT") {
        return `import OpenAI from "openai";
import { PromptLayer } from "promptlayer";

// Uses OPENAI_API_KEY from your environment / .env
const openai = new OpenAI();

const pl_client = new PromptLayer({
  apiKey: "${key}",
  enableTracing: true,
});

// wrapWithSpan is the JS equivalent of @traceable
const callLlm = pl_client.wrapWithSpan("llm.call", async (question) => {
  const completion = await openai.chat.completions.create({
    model: "gpt-5.4-nano",
    messages: [{ role: "user", content: question }],
  });

  return completion.choices[0]?.message?.content;
});

const runAgentTurn = pl_client.wrapWithSpan("agent.turn", async (question) => {
  return callLlm(question);
});

const answer = await runAgentTurn("What is PromptLayer?");
console.log(answer);`;
      }

      return `from openai import OpenAI
from promptlayer import PromptLayer

# Uses OPENAI_API_KEY from your environment / .env
openai = OpenAI()

pl_client = PromptLayer(api_key="${key}", enable_tracing=True)

@pl_client.traceable(name="llm.call")
def call_llm(question: str) -> str:
    completion = openai.chat.completions.create(
        model="gpt-5.4-nano",
        messages=[{"role": "user", "content": question}],
    )
    return completion.choices[0].message.content

@pl_client.traceable(name="agent.turn")
def run_agent_turn(question: str) -> str:
    return call_llm(question)

print(run_agent_turn("What is PromptLayer?"))`;
    },
  },
  {
    id: "opentelemetry",
    title: "OpenTelemetry",
    description: "Send traces from any OTEL-compatible SDK.",
    docsUrl: "https://docs.promptlayer.com/features/opentelemetry",
    logoProvider: "opentelemetry",
    languages: BOTH_LANGUAGES,
    getSnippet: (language, apiKey) => {
      const key = formatApiKey(apiKey);
      const endpoint = getOtlpEndpoint();

      if (language === "JAVASCRIPT") {
        return `import { NodeSDK } from "@opentelemetry/sdk-node";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";

const sdk = new NodeSDK({
  serviceName: "my-llm-app",
  resource: resourceFromAttributes({
    "service.name": "my-llm-app",
  }),
  traceExporter: new OTLPTraceExporter({
    url: "${endpoint}",
    headers: {
      "X-API-Key": "${key}",
    },
  }),
});

sdk.start();

process.on("beforeExit", async () => {
  await sdk.shutdown();
});`;
      }

      return `from opentelemetry.exporter.otlp.proto.http.trace_exporter import OTLPSpanExporter
from opentelemetry.sdk.resources import Resource
from opentelemetry.sdk.trace import TracerProvider
from opentelemetry.sdk.trace.export import BatchSpanProcessor

exporter = OTLPSpanExporter(
    endpoint="${endpoint}",
    headers={"X-API-KEY": "${key}"},
)

provider = TracerProvider(
    resource=Resource.create({"service.name": "my-llm-app"})
)
provider.add_span_processor(BatchSpanProcessor(exporter))

tracer = provider.get_tracer("my-llm-app")`;
    },
  },
  {
    id: "langchain",
    title: "LangChain",
    description: "Route LangSmith OTEL traces to PromptLayer.",
    docsUrl: "https://docs.promptlayer.com/features/integrations#langchain--langsmith",
    logoProvider: "langchain",
    languages: BOTH_LANGUAGES,
    getSnippet: (language, apiKey) => {
      const key = formatApiKey(apiKey);
      const endpoint = getOtlpEndpoint();

      if (language === "JAVASCRIPT") {
        return `// Set these env vars before starting your app:
// LANGSMITH_TRACING=true
// LANGSMITH_TRACING_MODE=otel
// LANGCHAIN_CALLBACKS_BACKGROUND=false
// OTEL_EXPORTER_OTLP_ENDPOINT=${endpoint}
// OTEL_EXPORTER_OTLP_HEADERS=X-API-KEY=${key}

import { context, trace } from "@opentelemetry/api";
import { AsyncLocalStorageContextManager } from "@opentelemetry/context-async-hooks";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-proto";
import { BasicTracerProvider, BatchSpanProcessor } from "@opentelemetry/sdk-trace-base";
import { initializeOTEL } from "langsmith/experimental/otel/setup";

const exporter = new OTLPTraceExporter({
  url: "${endpoint}",
  headers: { "X-API-KEY": "${key}" },
});
const provider = new BasicTracerProvider({
  spanProcessors: [new BatchSpanProcessor(exporter)],
});
const contextManager = new AsyncLocalStorageContextManager();

contextManager.enable();
context.setGlobalContextManager(contextManager);
trace.setGlobalTracerProvider(provider);

initializeOTEL({
  globalTracerProvider: provider,
  globalContextManager: contextManager,
  skipGlobalContextManagerSetup: true,
});`;
      }

      return `import os

os.environ["LANGSMITH_TRACING"] = "true"
os.environ["LANGSMITH_TRACING_MODE"] = "otel"
os.environ["LANGCHAIN_CALLBACKS_BACKGROUND"] = "false"
os.environ["OTEL_EXPORTER_OTLP_ENDPOINT"] = "${endpoint}"
os.environ["OTEL_EXPORTER_OTLP_HEADERS"] = "X-API-KEY=${key}"
os.environ["OTEL_SERVICE_NAME"] = "langchain-app"

# Then run your LangChain / LangGraph app as usual`;
    },
  },
  {
    id: "vercel-ai",
    title: "Vercel AI SDK",
    description: "Export AI SDK spans over OTLP/HTTP.",
    docsUrl: "https://docs.promptlayer.com/features/integrations#vercel-ai-sdk",
    logoProvider: "vercel",
    languages: JAVASCRIPT_ONLY,
    getSnippet: (_language, apiKey) => {
      const key = formatApiKey(apiKey);
      const endpoint = getOtlpEndpoint();

      return `import { NodeSDK } from "@opentelemetry/sdk-node";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { generateText } from "ai";
import { openai } from "@ai-sdk/openai";

const sdk = new NodeSDK({
  serviceName: "vercel-ai-app",
  resource: resourceFromAttributes({
    "promptlayer.telemetry.source": "vercel-ai-sdk",
  }),
  traceExporter: new OTLPTraceExporter({
    url: "${endpoint}",
    headers: {
      "X-API-Key": "${key}",
    },
  }),
});

sdk.start();

const result = await generateText({
  model: openai("gpt-4o-mini"),
  prompt: "Write a short poem about observability.",
  experimental_telemetry: {
    isEnabled: true,
    recordInputs: true,
    recordOutputs: true,
  },
});

console.log(result.text);

await sdk.shutdown();`;
    },
  },
  {
    id: "pydantic-ai",
    title: "Pydantic AI",
    description: "Instrument agents with Logfire and OTLP export.",
    docsUrl: "https://docs.promptlayer.com/features/integrations#pydantic-ai",
    logoProvider: "pydantic",
    languages: PYTHON_ONLY,
    getSnippet: (_language, apiKey) => {
      const key = formatApiKey(apiKey);
      const endpoint = getOtlpEndpoint();

      return `import os
import logfire

os.environ["OTEL_EXPORTER_OTLP_TRACES_ENDPOINT"] = "${endpoint}"
os.environ["OTEL_EXPORTER_OTLP_HEADERS"] = "X-API-KEY=${key}"
os.environ["OTEL_SERVICE_NAME"] = "pydantic-ai-app"

logfire.configure(send_to_logfire=False)
logfire.instrument_pydantic_ai()`;
    },
  },
  {
    id: "openai-agents",
    title: "OpenAI Agents SDK",
    description: "Send agent spans to PromptLayer via OTEL.",
    docsUrl: "https://docs.promptlayer.com/features/integrations#openai-agents-sdk",
    logoProvider: "openai",
    languages: BOTH_LANGUAGES,
    getSnippet: (language, apiKey) => {
      const key = formatApiKey(apiKey);

      if (language === "JAVASCRIPT") {
        return `// npm install promptlayer @openai/agents
// Set PROMPTLAYER_API_KEY=${key}

import { instrumentOpenAIAgents } from "promptlayer/openai-agents";

const processor = await instrumentOpenAIAgents();

// Run your agents, then flush before exit:
await processor.forceFlush();
await processor.shutdown();`;
      }

      return `# pip install "promptlayer[openai-agents]"
# Set PROMPTLAYER_API_KEY=${key}

from promptlayer.integrations.openai_agents import instrument_openai_agents

instrument_openai_agents()

# Then run your OpenAI Agents SDK app as usual`;
    },
  },
  {
    id: "claude-code",
    title: "Claude Code",
    description: "Forward Claude Code telemetry to PromptLayer.",
    docsUrl: "https://docs.promptlayer.com/features/integrations#claude-code",
    logoProvider: "anthropic",
    languages: BOTH_LANGUAGES,
    getSnippet: (language, apiKey) => {
      const key = formatApiKey(apiKey);

      if (language === "JAVASCRIPT") {
        return `// npm install promptlayer @anthropic-ai/claude-agent-sdk
// Set PROMPTLAYER_API_KEY=${key}

import type { Options } from "@anthropic-ai/claude-agent-sdk";
import { getClaudeConfig } from "promptlayer/claude-agents";

const plClaudeConfig = getClaudeConfig();

const options: Options = {
  model: "sonnet",
  cwd: process.cwd(),
  plugins: [plClaudeConfig.plugin],
  env: {
    ...process.env,
    ...plClaudeConfig.env,
  },
};`;
      }

      return `# pip install "promptlayer[claude-agents]"
# Set PROMPTLAYER_API_KEY=${key}

from claude_agent_sdk import ClaudeAgentOptions
from promptlayer.integrations.claude_agents import get_claude_config

pl_claude_config = get_claude_config()

options = ClaudeAgentOptions(
    model="sonnet",
    cwd=".",
    plugins=[pl_claude_config.plugin],
    env={**pl_claude_config.env},
)`;
    },
  },
  {
    id: "openclaw",
    title: "OpenClaw",
    description: "Export OpenClaw agent spans over OTLP.",
    docsUrl: "https://docs.promptlayer.com/features/integrations",
    logoProvider: "openclaw",
    languages: BASH_ONLY,
    getSnippet: (_language, apiKey) => {
      const key = formatApiKey(apiKey);

      return `openclaw plugins install @promptlayer/openclaw-promptlayer
openclaw plugins enable openclaw-promptlayer

export PROMPTLAYER_API_KEY="${key}"

# Then include "openclaw-promptlayer" in openclaw.json plugins`;
    },
  },
  {
    id: "livekit",
    title: "LiveKit",
    description: "Send LiveKit agent traces via GenAI OTEL conventions.",
    docsUrl: "https://docs.promptlayer.com/features/integrations",
    logoProvider: "livekit",
    languages: BOTH_LANGUAGES,
    getSnippet: (language, apiKey) => {
      const key = formatApiKey(apiKey);
      const endpoint = getOtlpEndpoint();

      if (language === "JAVASCRIPT") {
        return `import { telemetry } from "@livekit/agents";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-base";
import { NodeTracerProvider } from "@opentelemetry/sdk-trace-node";

const provider = new NodeTracerProvider({
  spanProcessors: [
    new BatchSpanProcessor(
      new OTLPTraceExporter({
        url: "${endpoint}",
        headers: { "X-API-KEY": "${key}" },
      }),
    ),
  ],
});

provider.register();
telemetry.setTracerProvider(provider);

// Call this before starting your LiveKit agent session`;
      }

      return `from livekit.agents import telemetry
from opentelemetry.exporter.otlp.proto.http.trace_exporter import OTLPSpanExporter
from opentelemetry.sdk.trace import TracerProvider
from opentelemetry.sdk.trace.export import BatchSpanProcessor

provider = TracerProvider()
provider.add_span_processor(
    BatchSpanProcessor(
        OTLPSpanExporter(
            endpoint="${endpoint}",
            headers={"X-API-KEY": "${key}"},
        )
    )
)

telemetry.set_tracer_provider(provider)

# Call this before starting your LiveKit agent session`;
    },
  },
];
